import { AuthorshipLine } from "@/components/molecules/AuthorshipLine";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IncludeExcludeToggle } from "@/components/ui/include-exclude-toggle";

export interface ItemCardProps {
  id: string;
  title: string;
  description?: string;
  isSelected: boolean;
  // Authorship, when the item type carries it. Domains, pages and policies do;
  // personas and offers are generated backend-side and have no member author,
  // so they simply pass nothing and the line is omitted.
  createdBy?: string | null;
  updatedBy?: string | null;
  // Where this item came from, when the assistant produced it — see
  // `conversationLinkFor`. Personas and offers have no member author at all, so
  // for them this is the only origin the card can show.
  conversationHref?: string | null;
  // Optional: some item types (personas, offers) are backend-write-only --
  // manual toggle/edit/delete isn't available for them, so these controls
  // are omitted entirely rather than shown broken.
  onToggle?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function ItemCard({
  id,
  title,
  description,
  isSelected,
  createdBy,
  updatedBy,
  conversationHref,
  onToggle,
  onEdit,
  onDelete,
}: ItemCardProps) {
  const hasControls = onToggle || onEdit || onDelete;
  return (
    <Card className="overflow-hidden py-3">
      <div className="flex items-stretch gap-3 px-3 md:px-4">
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-medium">{title}</h4>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
          <AuthorshipLine
            createdBy={createdBy}
            updatedBy={updatedBy}
            className="text-xs text-muted-foreground mt-1"
          />
          {conversationHref && (
            <Link
              to={conversationHref}
              className="mt-1 inline-block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              From a conversation
            </Link>
          )}
        </div>

        {hasControls && (
          <>
            <div className="w-px bg-gray-200 self-stretch" />

            <div className="flex flex-col gap-2 flex-shrink-0 w-24">
              {onToggle && (
                <IncludeExcludeToggle
                  id={`item-${id}`}
                  checked={isSelected}
                  onCheckedChange={() => onToggle(id)}
                />
              )}
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => onEdit(id)}
                >
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => onDelete(id)}
                >
                  Delete
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
